import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ProductCard } from '../components/ProductCard';
import { BrandsMarquee } from '../components/BrandsMarquee';
import { Shield, Zap, Truck, ArrowLeft, ArrowRight, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const { t, isArabic } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const ArrowIcon = isArabic ? ArrowLeft : ArrowRight;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API}/products?limit=8`);
        const data = await res.json();
        // Handle both old format (array) and new format (object with products)
        const productList = Array.isArray(data) ? data : (data.products || []);
        setProducts(productList.slice(0, 8));
      } catch (err) {
        console.error('Error fetching products:', err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  const features = [
    {
      icon: Shield,
      titleKey: 'feature1Title',
      descKey: 'feature1Desc',
    },
    {
      icon: Zap,
      titleKey: 'feature2Title',
      descKey: 'feature2Desc',
    },
    {
      icon: Truck,
      titleKey: 'feature3Title',
      descKey: 'feature3Desc',
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-center overflow-hidden pt-20 md:pt-0" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50" />
          
          {/* Decorative elements - hidden on mobile */}
          <div className="hidden md:block absolute top-20 right-10 w-72 h-72 bg-slate-100 rounded-full blur-3xl opacity-60" />
          <div className="hidden md:block absolute bottom-20 left-10 w-96 h-96 bg-slate-100 rounded-full blur-3xl opacity-40" />
          
          {/* Grid pattern */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className={isArabic ? 'lg:order-1' : 'lg:order-2'}
            >
              <span className="inline-block px-3 py-1.5 md:px-4 md:py-2 bg-black text-white text-xs font-medium rounded-full mb-4 md:mb-6 tracking-wider uppercase">
                {isArabic ? 'البصمة الحديثة لاستيراد المعدات الطبية' : 'ALBASMA ALHADITHA'}
              </span>
              
              <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 md:mb-6 leading-tight ${isArabic ? 'font-arabic' : 'font-english'}`}>
                {t('heroTitle')}
              </h1>
              
              <p className="text-base md:text-lg lg:text-xl text-slate-600 mb-3 md:mb-4 leading-relaxed">
                {t('heroSubtitle')}
              </p>
              
              <p className="text-sm md:text-base text-slate-500 mb-6 md:mb-8">
                {t('heroDesc')}
              </p>

              <div className="flex flex-wrap gap-3 md:gap-4">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center gap-2 group"
                  data-testid="hero-learn-more-btn"
                >
                  {t('learnMore')}
                  <ArrowIcon className="w-4 h-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                </Link>
                <Link
                  to="/products"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="hero-products-btn"
                >
                  {t('viewProducts')}
                </Link>
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={`relative ${isArabic ? 'lg:order-2' : 'lg:order-1'}`}
            >
              <div className="relative aspect-square max-w-lg mx-auto">
                {/* Decorative circle */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full" />
                
                {/* Image */}
                <div className="absolute inset-4 rounded-full overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=800&q=80"
                    alt="Medical Equipment"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Floating badges */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="absolute -left-4 top-1/4 bg-white shadow-xl rounded-2xl p-4 hidden md:block"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <span className={`text-sm font-medium text-slate-900 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                      {isArabic ? 'جودة موثوقة' : 'Certified Quality'}
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="absolute -right-4 bottom-1/4 bg-white shadow-xl rounded-2xl p-4 hidden md:block"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <Truck className="w-5 h-5 text-black" />
                    </div>
                    <span className={`text-sm font-medium text-slate-900 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                      {isArabic ? 'توصيل مجاني' : 'Free Delivery'}
                    </span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="flex flex-col items-center gap-2 text-slate-400"
          >
            <ChevronDown className="w-6 h-6" />
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="features-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className={`text-3xl md:text-4xl font-bold text-slate-900 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {t('featuresTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-colors duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-black flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className={`text-xl font-semibold text-slate-900 mb-3 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                  {t(feature.titleKey)}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t(feature.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Marquee */}
      <BrandsMarquee />

      {/* Wholesale Services Section */}
      <section className="py-16 md:py-24 bg-slate-900 text-white" data-testid="wholesale-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-white/10 text-white text-xs font-medium rounded-full mb-6 tracking-wider uppercase">
              {t('wholesaleTitle')}
            </span>
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {t('wholesaleSubtitle')}
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              {t('wholesaleDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '💰', titleKey: 'wholesaleFeature1' },
              { icon: '📦', titleKey: 'wholesaleFeature2' },
              { icon: '🛠️', titleKey: 'wholesaleFeature3' },
              { icon: '🚚', titleKey: 'wholesaleFeature4' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white/10 transition-all duration-300"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <p className={`font-medium ${isArabic ? 'font-arabic' : 'font-english'}`}>
                  {t(item.titleKey)}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <p className="text-slate-400 mb-6">
              {isArabic ? 'نخدم شركات الجملة للمعدات الطبية ومستلزمات الأم والطفل في جميع أنحاء ليبيا' : 'We serve wholesale companies for medical equipment and mother & baby supplies across Libya'}
            </p>
            <Link
              to="/contracts"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-900 font-semibold rounded-xl hover:bg-slate-100 transition-all duration-300"
              data-testid="wholesale-contact-btn"
            >
              {t('requestContract')}
              <ArrowIcon className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="products-preview-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
          >
            <div>
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-3 block">
                {isArabic ? 'تشكيلة متميزة' : 'Premium Selection'}
              </span>
              <h2 className={`text-3xl md:text-4xl font-bold text-slate-900 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                {t('productsTitle')}
              </h2>
              <p className="text-slate-600 mt-3">
                {t('productsSubtitle')}
              </p>
            </div>
            <Link
              to="/products"
              className="mt-6 md:mt-0 inline-flex items-center gap-2 text-black font-medium hover:gap-3 transition-all duration-300 group"
              data-testid="view-all-products-link"
            >
              {t('viewAll')}
              <ArrowIcon className="w-4 h-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full" />
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-500 mb-4">
                {isArabic ? 'لا توجد منتجات حالياً' : 'No products available'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black text-white" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                {isArabic ? 'هل تبحث عن شريك موثوق للمعدات الطبية؟' : 'Looking for a Trusted Medical Equipment Partner?'}
              </h2>
              <p className="text-slate-400 text-lg mb-8">
                {isArabic 
                  ? 'تواصل معنا اليوم لمعرفة كيف يمكننا خدمة احتياجاتك الطبية'
                  : 'Contact us today to learn how we can serve your medical needs'
                }
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-slate-100 transition-colors duration-300 inline-flex items-center gap-2"
                  data-testid="cta-contact-btn"
                >
                  {t('contact')}
                  <ArrowIcon className="w-4 h-4" />
                </Link>
                <Link
                  to="/contracts"
                  className="border border-white/30 text-white px-8 py-3 rounded-full font-medium hover:bg-white/10 transition-colors duration-300"
                  data-testid="cta-contracts-btn"
                >
                  {t('contracts')}
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
